import DeliveryTruckIcon from "@/components/icons/delivery-truck";
import PackageOpenIcon from "@/components/icons/package-open";
import { Order } from "@/types";
import cn from "@/utils/class-names";
import { PiCaretDownBold, PiPackageFill } from "react-icons/pi";
import { Collapse, Text, Title } from "rizzui";

export default function ShippingInfo({
    order,
    className,
}: {
    order: Order;
    className?: string;
}) {
    const shippingInformation = [
        {
            title: "Order Details",
            icon: <DeliveryTruckIcon className="h-5 w-6 text-primary" />,
            data: [
                {
                    name: "Order Number",
                    value: <strong>#{order?.orderId}</strong>,
                },
                {
                    name: "Client",
                    value: order?.client?.fullname,
                },
                {
                    name: "Requested Date",
                    value: new Date(order?.createdDate).toDateString(),
                },
            ],
        },
        {
            title: "Shipment Overview",
            icon: <PiPackageFill className="h-6 w-6 text-primary" />,
            data: [
                {
                    name: "Tracking Number",
                    value: order?.trackingNumber,
                },
                {
                    name: "Delivered To",
                    value: order?.shippingAddress,
                },
                {
                    name: "Shipping Date",
                    value: order?.shippingDate,
                },
                {
                    name: "Estimated Delivery",
                    value: order?.estimatedDeliveryDate,
                },
                {
                    name: "Service Provider",
                    value: "Example Service Provider",
                },
            ],
        },
        {
            title: "Product Details",
            icon: <PackageOpenIcon className="h-5 w-5 text-primary" />,
            data: [
                {
                    name: "Name",
                    value: order?.product?.name,
                },
                {
                    name: "Category",
                    value: order?.product?.category?.name,
                },
                {
                    name: "Supplier",
                    value: order?.product?.supplier,
                },
                {
                    name: "Weight",
                    value: order?.product?.weight,
                },
                {
                    name: "Price",
                    value: "$" + order?.product?.price,
                },
                {
                    name: "Quantity",
                    value: "$" + order?.quantity,
                },
                {
                    name: "Total Price",
                    value: "$" + order?.totalPrice,
                },
            ],
        },
    ];

    return (
        <>
            <Collapse
                defaultOpen={true}
                className={cn("mx-0 py-5 md:py-7 lg:mx-8", className)}
                header={({ open, toggle }) => (
                    <button
                        type="button"
                        onClick={toggle}
                        className="flex w-full cursor-pointer items-center justify-between text-left font-lexend text-xl font-semibold text-gray-700"
                    >
                        Shipping Information
                        <PiCaretDownBold
                            className={cn(
                                "h-5 w-5 -rotate-90 transform transition-transform duration-300 rtl:rotate-90",
                                open && "-rotate-0 rtl:rotate-0",
                            )}
                        />
                    </button>
                )}
            >
                {shippingInformation.map((item, index) => (
                    <div
                        className={cn(
                            "my-10 flex gap-4",
                            index === shippingInformation.length - 1 && "mb-3",
                        )}
                        key={`shipping-block-${index}`}
                    >
                        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-lighter">
                            {item.icon}
                        </span>

                        <div className="flex flex-col gap-y-3">
                            <Title as="h3" className="text-base font-semibold">
                                {item.title}
                            </Title>
                            {item.data.map((info, index) => (
                                <div
                                    className="flex flex-col sm:flex-row sm:items-center"
                                    key={`info-${index}`}
                                >
                                    <Title
                                        as="h4"
                                        className="text-sm font-normal capitalize text-gray-700 sm:min-w-[244px] md:min-w-[424px]"
                                    >
                                        {info.name}:
                                    </Title>
                                    <Text className="gap-3 text-sm text-gray-500">
                                        {info.value}
                                    </Text>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </Collapse>
        </>
    );
}
